// dependencies
import React, { useEffect, useState } from 'react'
import ReactJson from 'react-json-view'

// components
import {
  Input,
  CircularLoader,
  Switch,
  Table,
  Button,
  BUTTON_THEME,
} from '@st-operations/uikit/src/v2/index.js'
import LoadingModal from '_components/loading-modal/index.jsx'
import PartnersCpm from './components/PartnersCpm.jsx'
import Header from '_components/header/index.jsx'

// services
import { getEntity, getSchema, updateEntity } from '_services/ttd-health-audiences.js'

//hooks
import { useRetractSidebar } from '_hooks'

// constants
import { ENTITY, INITIAL_STATE, TABLE_COLUMNS_SINGULAR } from '../constants'
import { ENTITY as TTD_AUDIENCE_ENTITY } from '../../ttd-audiences/constants'

// styles
import './styles.scss'
import { logErrorAsync } from '../../../lib/utility.js'

const TTDHealthAudience = (props) => {
  const { id } = props.match.params

  const [schema, setSchema] = useState(null)
  const [data, setData] = useState(null)
  const [fieldsValues, setFieldsValues] = useState(INITIAL_STATE)
  const [cpmRates, setCpmRates] = useState([])

  const [hasChanges, setHasChanges] = useState(false)
  const [isJSONView, setIsJSONView] = useState(false)
  const [inRequest, setInRequest] = useState(false)

  useRetractSidebar()

  const loadData = async () => {
    try {
      const data = await getEntity(ENTITY, id)
      setData(data)
    } catch (error) {
      logErrorAsync(error)
      toast.error(error.message)
    }
  }

  const loadSchema = async () => {
    try {
      const schema = await getSchema(ENTITY)
      setSchema(schema)
    } catch (error) {
      logErrorAsync(error)
      toast.error(error.message)
    }
  }

  const populateFields = (data) => {
    const fieldsKey = Object.keys(INITIAL_STATE)
    const fieldsData = fieldsKey.reduce(
      (newState, key) => ({ ...newState, [key]: data[key] ?? '' }),
      {}
    )
    setFieldsValues(fieldsData)
  }

  const formatCpmRatesIn = () => {
    let partnerCpmRates
    if (data.partnerCpmRates) {
      partnerCpmRates = data.partnerCpmRates
    } else {
      // TODO: handle backwards compatibility. Can be removed later
      partnerCpmRates = data.partnerIds.map((id) => ({
        partner: id,
        cpmRate: data.cpmRate,
      }))
    }

    const partnersIdByCpm = partnerCpmRates.reduce(
      (acc, { cpmRate, partner }) => ({ ...acc, [cpmRate]: [...(acc[cpmRate] ?? []), partner] }),
      {}
    )
    const formatedCpmRates = Object.entries(partnersIdByCpm).map(([cpm, partnersId], i) => ({
      id: i,
      cpm,
      partnersId,
    }))
    setCpmRates(formatedCpmRates)
  }

  const formatCpmRatesOut = (cpmRates) => {
    return cpmRates.reduce((acc, { cpm, partnersId }) => {
      const newItems = partnersId.map((id) => ({
        partner: id,
        cpmRate: cpm,
      }))
      return [...acc, ...newItems]
    }, [])
  }

  useEffect(() => {
    if (data) {
      populateFields(data)
      formatCpmRatesIn()
    } else {
      loadData()
    }
  }, [data])

  useEffect(() => {
    if (!schema) {
      loadSchema()
    }
  }, [schema])

  const handleInputChange = (value, name) => {
    setFieldsValues((fieldsValues) => ({ ...fieldsValues, [name]: value }))
    setHasChanges(true)
  }

  const handleExit = () => props.history.push(`/${ENTITY}`)

  const handleSave = async () => {
    setInRequest('Saving...')

    const partnerCpmRates = formatCpmRatesOut(cpmRates)
    const payload = { ...fieldsValues, partnerCpmRates }

    try {
      await updateEntity(ENTITY, id, payload)
      toast.success('Audience saved!')
      handleExit()
    } catch (error) {
      logErrorAsync(error)
      toast.error(error.message)
    }

    setInRequest(false)
  }

  const headerActions = [
    {
      children: 'Exit',
      onClick: handleExit,
      theme: BUTTON_THEME.GHOST,
    },
    {
      children: 'Save',
      onClick: handleSave,
      disabled: !hasChanges,
    },
  ]

  if (!(schema && data)) {
    return (
      <div className="ttd-health-audience__loading">
        <CircularLoader />
      </div>
    )
  }

  return (
    <div id="ttd-health-audience-page" className="ttd-health-audience">
      <LoadingModal condition={!!inRequest} message={inRequest} />
      <Header actions={headerActions} back={handleExit}>
        <h1 className="ttd-health-audience__title">{fieldsValues.name}</h1>
      </Header>
      <Input label="Name" name="name" onChange={handleInputChange} value={fieldsValues.name} />
      <Input
        label="Condition"
        name="condition"
        onChange={handleInputChange}
        value={fieldsValues.condition}
      />
      <div className="ttd-health-audience__input-button-wrapper">
        <Input label="ID" name="id" onChange={handleInputChange} value={fieldsValues.id} />
        <Button
          onClick={() => props.history.push(`/${TTD_AUDIENCE_ENTITY}/${fieldsValues.id}`)}
          disabled={!fieldsValues.id}
          theme={BUTTON_THEME.GHOST}
        >
          TTD Audience
        </Button>
      </div>
      <PartnersCpm
        schema={schema}
        cpmRates={cpmRates}
        setCpmRates={setCpmRates}
        setHasChanges={setHasChanges}
      />
      <div className="ttd-health-audience__batches-wrapper">
        <h3 className="ttd-health-audience__batches-label">Data Rate Batches</h3>
        <div className="ttd-health-audience__json-switch">
          <Switch checked={isJSONView} onChange={() => setIsJSONView((state) => !state)} />
          <span className="data-preview__switch-label">JSON View</span>
        </div>
      </div>
      <Choose>
        <When condition={isJSONView}>
          <ReactJson name={false} displayDataTypes={false} src={data.batches} />
        </When>
        <Otherwise>
          <Table columns={TABLE_COLUMNS_SINGULAR} data={data.batches} />
        </Otherwise>
      </Choose>
    </div>
  )
}

export default TTDHealthAudience
